import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { z } from "zod";
import {
  listReportableShifts, listMyIncidents, reportIncident, getEvidenceUploadTarget,
  EVIDENCE_MAX_BYTES, EVIDENCE_ALLOWED_MIME,
} from "@/lib/incidents.functions";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const TYPES = [
  { value: "safety_concern", label: "Safety concern" },
  { value: "illness", label: "Illness" },
  { value: "behavioral", label: "Behavioural" },
  { value: "aggression", label: "Aggression / abuse" },
  { value: "theft", label: "Theft (venue only)" },
  { value: "early_termination", label: "Shift ended early" },
  { value: "left_early", label: "Left early without notice" },
  { value: "other", label: "Other" },
] as const;

const searchSchema = z.object({ shift: z.string().uuid().optional() });

export const Route = createFileRoute("/_authenticated/incidents/new")({
  head: () => ({ meta: [{ title: "Report an Incident — The Hospitality Reserve" }] }),
  validateSearch: searchSchema,
  component: ReportPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-destructive">Could not load: {error.message}</div>
  ),
  notFoundComponent: () => <div className="p-8 text-sm">Not found.</div>,
});

type EligibleShift = {
  id: string; role_type: string; start_time: string; end_time: string;
  address: string | null; role: "venue" | "bartender";
};
type IncidentRow = {
  id: string; shift_id: string; type: string; description: string;
  status: string; resolution: string | null; resolved_at: string | null;
  created_at: string; reporter_id: string; reported_id: string | null;
  reporter_role: string;
};

function ReportPage() {
  const search = useSearch({ from: "/_authenticated/incidents/new" });
  const loadShifts = useServerFn(listReportableShifts);
  const loadMine = useServerFn(listMyIncidents);
  const submit = useServerFn(reportIncident);
  const getUpload = useServerFn(getEvidenceUploadTarget);

  const [shifts, setShifts] = useState<EligibleShift[]>([]);
  const [mine, setMine] = useState<IncidentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [shiftId, setShiftId] = useState<string>(search.shift ?? "");
  const [type, setType] = useState<typeof TYPES[number]["value"]>("safety_concern");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([loadShifts(), loadMine()]);
      setShifts(s.shifts as EligibleShift[]);
      setMine(m.incidents as IncidentRow[]);
      if (!shiftId && (s.shifts as EligibleShift[]).length === 1) {
        setShiftId((s.shifts as EligibleShift[])[0].id);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const onPickFiles = (list: FileList | null) => {
    const picked = Array.from(list ?? []).slice(0, 10);
    const ok: File[] = [];
    for (const f of picked) {
      if (!(EVIDENCE_ALLOWED_MIME as readonly string[]).includes(f.type)) {
        toast.error(`${f.name}: unsupported file type. Use JPEG/PNG/WebP/HEIC or PDF.`);
        continue;
      }
      if (f.size > EVIDENCE_MAX_BYTES) {
        toast.error(`${f.name}: exceeds ${EVIDENCE_MAX_BYTES / (1024 * 1024)} MB limit.`);
        continue;
      }
      ok.push(f);
    }
    setFiles(ok);
  };

  const selectedShift = shifts.find((s) => s.id === shiftId) ?? null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftId) { toast.error("Pick a shift first."); return; }
    if (description.trim().length < 10) { toast.error("Please give at least 10 characters of detail."); return; }
    setSubmitting(true);
    try {
      const evidence_urls: string[] = [];
      for (const f of files) {
        const target = await getUpload({
          data: { filename: f.name, content_type: f.type, size_bytes: f.size },
        });
        const up = await supabase.storage.from(target.bucket).upload(target.path, f, {
          upsert: false, contentType: f.type,
        });
        if (up.error) throw up.error;
        evidence_urls.push(target.path);
      }
      await submit({ data: { shift_id: shiftId, type, description: description.trim(), evidence_urls } });
      toast.success("Incident report submitted. Our team will review it.");
      setDescription("");
      setFiles([]);
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="tracking-wide-luxury text-accent">Trust &amp; Safety</p>
        <h1 className="mt-3 font-display text-3xl">Report an incident</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl">
          Use this form for safety concerns, behavioural issues, theft, illness or anything else that
          needs admin review on a recent shift. Reports are confidential. The shift is automatically
          flagged and any payout is held until our team resolves it.
        </p>
        <div className="gold-divider mt-4" />

        <form onSubmit={onSubmit} className="mt-8 space-y-6 border border-border p-6 bg-card">
          <div>
            <Label className="text-xs tracking-wide-luxury text-muted-foreground">Shift</Label>
            {loading ? (
              <p className="text-sm text-muted-foreground mt-2">Loading…</p>
            ) : shifts.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-2">
                No shifts are currently in the reporting window (active or recently ended).
                If you need to report a historic issue, contact admin@thehospitalityreserve.com.
              </p>
            ) : (
              <>
                <select
                  value={shiftId}
                  onChange={(e) => setShiftId(e.target.value)}
                  className="mt-2 w-full rounded-none border border-border bg-background px-3 py-2 text-sm">
                  <option value="">— Select a shift —</option>
                  {shifts.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.role_type} · {new Date(s.start_time).toLocaleString("en-GB")} ({s.role})
                    </option>
                  ))}
                </select>
                {selectedShift && (
                  <div className="mt-3 border border-accent/40 bg-accent/5 p-3 text-xs">
                    <p className="tracking-wide-luxury uppercase text-[0.6rem] text-accent">Linked to shift</p>
                    <p className="mt-1 font-display text-sm">
                      {selectedShift.role_type} · {new Date(selectedShift.start_time).toLocaleString("en-GB")} → {new Date(selectedShift.end_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                    {selectedShift.address && (
                      <p className="text-muted-foreground">{selectedShift.address}</p>
                    )}
                    <p className="mt-1 text-[0.65rem] font-mono text-muted-foreground">ref {selectedShift.id.slice(0, 8)}</p>
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <Label className="text-xs tracking-wide-luxury text-muted-foreground">Type</Label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as typeof type)}
              className="mt-2 w-full rounded-none border border-border bg-background px-3 py-2 text-sm">
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs tracking-wide-luxury text-muted-foreground">
              What happened? <span className="text-destructive">*</span>
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              maxLength={4000}
              className="mt-2 rounded-none"
              placeholder="Be specific: what occurred, who was involved, time/place, and any immediate actions taken." />
            <p className="mt-1 text-[10px] text-muted-foreground">{description.length}/4000</p>
          </div>

          <div>
            <Label className="text-xs tracking-wide-luxury text-muted-foreground">Evidence (optional)</Label>
            <input
              type="file"
              multiple
              accept={(EVIDENCE_ALLOWED_MIME as readonly string[]).join(",")}
              onChange={(e) => onPickFiles(e.target.files)}
              className="mt-2 block text-sm" />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Up to 10 files · JPEG/PNG/WebP/HEIC or PDF · max {EVIDENCE_MAX_BYTES / (1024 * 1024)} MB each.
              Files are queued for virus scanning and are only visible to admins during review.
            </p>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                {files.map((f, i) => (
                  <li key={i} className="font-mono">{f.name} · {(f.size / 1024).toFixed(0)} KB</li>
                ))}
              </ul>
            )}
          </div>

          <Button type="submit" disabled={submitting || !shiftId}
            className="rounded-none tracking-wide-luxury">
            {submitting ? "Submitting…" : "Submit report"}
          </Button>
        </form>

        <section className="mt-12">
          <h2 className="font-display text-xl">Your filed reports</h2>
          <div className="gold-divider mt-2" />
          {mine.length === 0 && (
            <p className="text-sm text-muted-foreground mt-4">No reports yet.</p>
          )}
          <ul className="mt-4 space-y-3">
            {mine.map((r) => (
              <li key={r.id} className="border border-border p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 border border-border text-[0.6rem] tracking-wide-luxury uppercase">{r.type.replace(/_/g, " ")}</span>
                  <span className={`px-2 py-0.5 border text-[0.6rem] tracking-wide-luxury uppercase ${r.status === "pending" ? "border-accent text-accent" : "border-emerald-700 text-emerald-700"}`}>{r.status}</span>
                  <span className="text-[0.65rem] text-muted-foreground">
                    {new Date(r.created_at).toLocaleString("en-GB")}
                    {" · "}
                    {r.reporter_id === r.reported_id ? "you" : r.reporter_role === "venue" ? "you reported" : "filed against you"}
                  </span>
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap line-clamp-3">{r.description}</p>
                {r.resolution && (
                  <p className="mt-2 text-xs text-muted-foreground">Outcome: <b>{r.resolution}</b></p>
                )}
              </li>
            ))}
          </ul>
        </section>

        <p className="mt-8 text-xs text-muted-foreground">
          For an emergency happening right now, use the &ldquo;End shift early&rdquo; button on the live
          shift card instead — it stops the shift immediately. <Link to="/dashboard" className="underline">Back to dashboard</Link>.
        </p>
      </main>
    </div>
  );
}
